/**
 * #789 司机位置上报（方案 B：完整 GPS 上报）
 *
 * 数据流：司机端 DriverLocationReporter（navigator.geolocation.watchPosition）
 *   → upsertDriverLocation 写 localStorage `changfa_driver_locations`
 *   → syncEngine（白名单键，HOT_PUSH_DOMAINS 热推）同步到服务器
 *   → 老板端 MapPage 司机模式读取并在高德地图渲染 Marker + 拨号 + 导航。
 *
 * 存储形态：记录型数组（每项含稳定 id = 司机账号 username），
 * syncEngine 对记录型数组做记录级 diff——多台设备（多个司机）各自上报
 * 不会互相覆盖，按记录 LWW 合并。
 */

export const DRIVER_LOCATIONS_KEY = 'changfa_driver_locations';

/** 司机位置「在线」判定窗口：5 分钟内有上报视为在线 */
export const DRIVER_FRESH_WINDOW_MS = 5 * 60 * 1000;

export interface DriverLocationRecord {
  /** 稳定 id：司机账号 username（记录级 diff / 多端合并的 key） */
  id: string;
  /** 司机显示名（displayName） */
  name: string;
  /** GCJ-02 纬度（已从 WGS-84 转换，可直接上高德） */
  lat: number;
  /** GCJ-02 经度 */
  lng: number;
  /** GPS 精度（米） */
  accuracy?: number;
  /** 速度（km/h，浏览器 m/s 换算而来） */
  speed?: number;
  /** 上报时间（本地毫秒时间戳） */
  updatedAt: number;
}

export function loadDriverLocations(): DriverLocationRecord[] {
  try {
    const raw = localStorage.getItem(DRIVER_LOCATIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is DriverLocationRecord =>
        x && typeof x === 'object' &&
        typeof x.id === 'string' && typeof x.lat === 'number' && typeof x.lng === 'number' && typeof x.updatedAt === 'number'
    );
  } catch {
    return [];
  }
}

/** 本地/远端（同步引擎应用）写入后派发的事件名（与 LS_KEY_EVENTS 保持一致） */
export const DRIVER_LOCATIONS_EVENT = 'changfa:driver-locations-changed';

/**
 * 上报/合并一条司机位置（按 id upsert）。
 * ⚠️ 直接 localStorage.setItem——syncEngine 已 patch Storage.prototype.setItem，
 * 命中白名单域即自动 notifyWrite（司机位置在 HOT_PUSH_DOMAINS 中，立即热推）。
 */
export function upsertDriverLocation(rec: DriverLocationRecord): void {
  const list = loadDriverLocations();
  const idx = list.findIndex((x) => x.id === rec.id);
  if (idx >= 0) list[idx] = rec;
  else list.push(rec);
  localStorage.setItem(DRIVER_LOCATIONS_KEY, JSON.stringify(list));
  // 本机 UI（老板端开着司机位置页时）立即刷新
  window.dispatchEvent(new CustomEvent(DRIVER_LOCATIONS_EVENT));
}

/** 记录是否「在线」（上报时间在窗口内） */
export function isDriverRecordFresh(rec: DriverLocationRecord, now = Date.now()): boolean {
  return now - rec.updatedAt < DRIVER_FRESH_WINDOW_MS;
}

/** 「x 分钟前」格式化 */
export function formatAgoTime(ts: number, now = Date.now()): string {
  const diff = Math.max(0, now - ts);
  if (diff < 60 * 1000) return '刚刚';
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)} 分钟前`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / 3600000)} 小时前`;
  return `${Math.floor(diff / 86400000)} 天前`;
}
